/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import { type ForwardedRef, createContext, forwardRef, useContext, useRef } from 'react';
import {
  type ContextValue,
  DateFieldStateContext,
  DateInput as DateInputCore,
  type DateInputProps as DateInputCoreProps,
  DatePickerStateContext,
  DateSegment as DateSegmentCore,
  Group as GroupCore,
  TimeFieldStateContext,
  useContextProps
} from 'react-aria-components';
import { CalendarStrokeIcon } from '../../icons/calendar-stroke/calendar-stroke.icon';
import { $ } from '../../tokens.stylex';
import { IconButton } from '../icon-button/icon-button.component';
import { Text } from '../text/text.component';
import { View } from '../view/view.component';

interface DateInputProps extends Omit<DateInputCoreProps, 'className' | 'style' | 'children'> {
  /** The size variant of the input. (Default: 48) */
  size?: keyof typeof sizeVariants;
  /** Placeholder text shown when there is no value and the input is not focused. */
  placeholder?: string;
  style?: stylex.StyleXStyles;
}

const DateInputContext = createContext<ContextValue<DateInputProps, HTMLDivElement>>({});

const DateInput = forwardRef((props: DateInputProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, DateInputContext);

  const datePickerState = useContext(DatePickerStateContext);
  const dateFieldState = useContext(DateFieldStateContext);
  const timeFieldState = useContext(TimeFieldStateContext);

  const { style, size = 48, placeholder = timeFieldState ? 'Select time' : 'Select date', ...coreProps } = props;

  const stateValue = datePickerState?.value ?? dateFieldState?.value ?? timeFieldState?.value;

  const contentRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  // When overlay text is visible, clicks land on the wrapper. We always focus
  // the first editable segment to avoid confusion because formatted text can
  // reorder date/time segments (e.g., clicking time could focus day).
  const focusFirstSegment = () => {
    const container = contentRef.current;
    if (!container) return;

    const firstSegment = container.querySelector<HTMLElement>('[data-type]:not([data-type="literal"])');
    if (firstSegment) {
      firstSegment.focus();
      return;
    }

    const firstFocusable = container.querySelector<HTMLElement>('[tabindex]');
    firstFocusable?.focus();
  };

  return (
    <GroupCore
      role="presentation"
      onPointerDownCapture={(event) => {
        if (event.button !== 0) return;

        const target = event.target as Node;
        if (contentRef.current?.contains(target)) return;
        if (buttonRef.current?.contains(target)) return;

        event.preventDefault();
        requestAnimationFrame(() => focusFirstSegment());
      }}
      {...stylex.props(
        stylex.defaultMarker(),
        styles.base,
        sizeVariants[size],
        datePickerState && datePickerSizeVariants[size],
        variantVariants.outlined,
        style)}>
      {(renderProps) => {
        let lastNonLiteralIsPlaceholder = true;

        const isButtonFocused =
          typeof document !== 'undefined' &&
          !!buttonRef.current &&
          document.activeElement === buttonRef.current;

        const shouldShowPlaceholder =
          !stateValue &&
          (!renderProps.isFocusWithin || datePickerState?.isOpen || isButtonFocused);

        return (
          <>
            <View
              ref={contentRef}
              style={contentStyles.base}
              onPointerDownCapture={(event) => {
                if (event.button !== 0) return;

                if (shouldShowPlaceholder) {
                  event.preventDefault();
                  requestAnimationFrame(focusFirstSegment);
                }
              }}>
              {shouldShowPlaceholder && (
                <Text
                  slot={null!}
                  size={size === 48 ? 16 : 14}
                  style={placeholderStyles.base}>
                  {placeholder}
                </Text>
              )}
              <DateInputCore
                ref={ref}
                {...stylex.props(inputStyles.base, shouldShowPlaceholder && inputStyles.hidden)}
                {...coreProps}>
                {(segment) => {
                  if (segment.type !== 'literal') {
                    lastNonLiteralIsPlaceholder = segment.isPlaceholder;
                  }

                  const literalShouldBePlaceholder =
                    segment.type === 'literal' && lastNonLiteralIsPlaceholder;

                  return (
                    <DateSegmentCore
                      {...stylex.props(
                        dateSegmentStyles.base,
                        dateSegmentSizeVariants[size],
                        literalShouldBePlaceholder && dateSegmentStyles.literalPlaceholder
                      )}
                      segment={segment}
                    />
                  );
                }}
              </DateInputCore>
            </View>
            {datePickerState &&
              <IconButton
                ref={buttonRef}
                aria-label="Open calendar"
                style={(iconButtonRenderProps) => [
                  iconButtonStyles.base,
                  iconButtonSizeVariants[size],
                  iconButtonRenderProps.isDisabled && iconButtonStyles.disabled
                ]}
                size={size === 32 ? 24 : 32}
                variant="ghostLowContrast">
                <CalendarStrokeIcon />
              </IconButton>}
          </>
        );
      }}
    </GroupCore>
  );
});

const styles = stylex.create({
  base: {
    borderStyle: 'solid',
    overflow: 'hidden',
    alignItems: 'center',
    boxSizing: 'border-box',
    columnGap: 12,
    cursor: {
      default: 'text',
      ':is([data-disabled])': 'not-allowed',
    },
    display: 'flex',
  },
});

const inputStyles = stylex.create({
  base: {
    alignItems: 'center',
    color: {
      default: $.textPrimary,
      ':is([data-disabled])': $.textDisabled,
      ':is([data-placeholder])': $.textPlaceholder,
    },
    display: 'flex',
  },
  hidden: {
    opacity: 0,
  },
});

const dateSegmentStyles = stylex.create({
  base: {
    borderRadius: 3,
    outline: 'none',
    backgroundColor: {
      default: 'transparent',
      ':is([data-focused])': $.surfaceHighlightMedium,
    },
    color: {
      default: $.textPrimary,
      ':is([data-disabled])': $.textDisabled,
      ':is([data-placeholder])': $.textPlaceholder,
    },
    fontFamily: 'Inter',
    fontFeatureSettings: 'liga off, clig off',
    fontStyle: 'normal',
    fontWeight: 400,
    '::placeholder': { color: $.textPlaceholder },
    '::selection': { backgroundColor: $.surfaceHighlightMedium },
  },
  literalPlaceholder: {
    color: $.textPlaceholder,
  },
});

const contentStyles = stylex.create({
  base: {
    flex: '1 1 auto',
    alignItems: 'center',
    display: 'flex',
    position: 'relative',
    minWidth: 0,
  },
});

const placeholderStyles = stylex.create({
  base: {
    overflow: 'hidden',
    color: {
      default: $.textPlaceholder,
      [stylex.when.ancestor(':is([data-disabled])')]: $.textDisabled,
      [stylex.when.ancestor(':is([data-hovered])')]: $.textQuaternary,
    },
    pointerEvents: 'none',
    position: 'absolute',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    left: 0,
    right: 0,
  },
});

const sizeVariants = stylex.create({
  48: {
    borderRadius: 10,
    paddingBlock: {
      default: 5,
      [stylex.when.ancestor(':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])')]: 4,
    },
    paddingInline: {
      default: 14,
      [stylex.when.ancestor(':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])')]: 13,
    },
    height: 48,
  },
  32: {
    borderRadius: 8,
    paddingInline: {
      default: 12,
      [stylex.when.ancestor(':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])')]: 11,
    },
    height: 32,
  },
});

const datePickerSizeVariants = stylex.create({
  48: {
    paddingRight: {
      default: 12,
      [stylex.when.ancestor(':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])')]: 11,
    },
  },
  32: {
    paddingRight: {
      default: 10,
      [stylex.when.ancestor(':is([data-focused], [data-focus-within], [data-invalid], [aria-expanded="true"])')]: 9,
    },
  },
});

const iconButtonSizeVariants = stylex.create({
  48: {
    marginRight: -4,
  },
  32: {
    marginRight: -6
  },
});

const iconButtonStyles = stylex.create({
  base: {
    marginRight: -4,
  },
  disabled: {
    backgroundColor: 'transparent'
  }
});

const variantVariants = stylex.create({
  outlined: {
    borderColor: {
      default: $.border16,
      ':is([data-disabled])': $.border10,
      ':is([data-focused], [data-focus-within]):not([data-invalid]):not([data-button-pressed]):not([data-button-focused])': $.borderInteractive,
      ':is([data-hovered]):not([data-focused], [data-focus-within], [data-invalid], [data-disabled])': $.border24,
      ':is([data-invalid])': $.borderCritical,
    },
    borderWidth: 1,
    backgroundColor: $.surfaceBGPrimary,
    boxShadow: {
      default: 'none',
      ':is([data-focused], [data-focus-within]):not([data-invalid]):not([data-button-pressed]):not([data-button-focused])': `0 0 0 1px ${$.borderInteractive}`,
      ':is([data-invalid])': `0 0 0 1px ${$.borderCritical}`,
    },
  },
});

const dateSegmentSizeVariants = stylex.create({
  48: {
    fontSize: 16,
    letterSpacing: -0.5,
  },
  32: {
    fontSize: 14,
    letterSpacing: -0.45,
  },
});

export { DateInput, DateInputContext, type DateInputProps };
